import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, ShieldAlert } from 'lucide-react';
import { Button } from "@/components/common/ui/Button.tsx";
import { Input } from '@/components/common/ui/Input';
import { usePasswordReset } from '@/hooks/auth/usePasswordReset';
import { useAuth } from '@clerk/clerk-react';

const ForgotPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const navigate = useNavigate();
    const { requestReset, isLoading } = usePasswordReset();
    const { signOut } = useAuth();

    // 1. Terminate any active sessions when the user initiates the forgot password flow
    useEffect(() => {
        const purgeSessions = async () => {
            try {
                await signOut();
            } catch (err) {
                console.error("Failed to purge lingering sessions", err);
            }
        };
        purgeSessions();
    }, [signOut]);

    const validateEmail = (email: string): boolean => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email) {
            setError('Email is required');
            return;
        }

        if (!validateEmail(email)) {
            setError('Invalid email format');
            return;
        }

        const result = await requestReset(email);
        if (result.success) {
            setIsSubmitted(true);
        }
    };

    const handleGoToReset = () => {
        navigate('/auth/reset-password');
    };

    if (isSubmitted) {
        return (
            <div className="space-y-6 font-mono max-w-md mx-auto p-8 bg-white dark:bg-[#050505] border border-border-light dark:border-[#1A1D24] rounded-2xl shadow-lg dark:shadow-[0_0_50px_rgba(0,0,0,0.8)] relative overflow-hidden transition-all duration-300 text-center">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#02DFC2] to-transparent shadow-[0_0_10px_#02DFC2]"></div>
                
                <div className="flex justify-center mb-6">
                    <div className="h-16 w-16 bg-[#02DFC2]/10 border border-[#02DFC2]/30 rounded-full flex items-center justify-center shadow-[inset_0_0_15px_rgba(2,223,194,0.1)]">
                        <Mail className="w-8 h-8 text-[#02DFC2]" />
                    </div>
                </div>

                <div className="space-y-2 mb-8">
                    <h1 className="text-md font-bold text-text-primary-light dark:text-white tracking-[0.2em] uppercase">
                        Check your email
                    </h1>
                    <p className="text-[11px] text-text-secondary-light dark:text-slate-500 uppercase tracking-widest leading-relaxed max-w-sm mx-auto">
                        We've sent a verification code to <br/><p className="text-[#02DFC2] lowercase">{email}</p>
                    </p>
                </div>

                <div className="space-y-5">
                    <Button
                        onClick={handleGoToReset}
                        className="w-full bg-[#02DFC2] text-black font-black tracking-[0.2em] h-11 shadow-[0_0_15px_rgba(2,223,194,0.2)] hover:bg-[#00c9ae]"
                        size="lg"
                    >
                        ENTER VERIFICATION CODE
                    </Button>

                    <div className="text-[10px] text-slate-500 uppercase tracking-widest space-y-2">
                        <p>Didn't receive the email? Check your spam folder or</p>
                        <button
                            onClick={() => setIsSubmitted(false)}
                            className="text-[#02DFC2] hover:text-white transition-colors duration-200 border-b border-[#02DFC2]/30 hover:border-white"
                        >
                            Try again
                        </button>
                    </div>

                    <div className="pt-4 border-t border-[#1A1D24]">
                        <Link
                            to="/auth/login"
                            className="inline-flex items-center space-x-2 text-[10px] text-slate-500 hover:text-[#02DFC2] transition-colors duration-200 uppercase tracking-widest"
                        >
                            <ArrowLeft className="w-3 h-3" />
                            <span>Back to Login</span>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 font-mono max-w-md mx-auto p-6 bg-white dark:bg-[#050505] border border-border-light dark:border-[#1A1D24] rounded-2xl shadow-lg dark:shadow-[0_0_50px_rgba(0,0,0,0.8)] relative overflow-hidden transition-all duration-300">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#02DFC2] to-transparent shadow-[0_0_10px_#02DFC2]"></div>
            
            {/* Header / Identity Logo */}
            <div className="text-center space-y-3">
                <div className="flex justify-center mb-2">
                    <Link to="/" className="flex items-center z-50">
                        <img 
                            src="/image/logo.png" 
                            alt="ThreatInformant" 
                            className="h-20 w-52 object-contain"
                        />
                    </Link>
                </div>
                <h1 className="text-md font-bold text-text-primary-light dark:text-white tracking-[0.09em] flex items-center justify-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-[#02DFC2]" /> Forgot Password ?
                </h1>
                <p className="text-[10px] text-text-secondary-light dark:text-slate-500 uppercase tracking-widest leading-relaxed">
                    Enter your email address and we'll send you a verification code to reset your password
                </p>
            </div>

            {/* Form Matrix */}
            <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                    }}
                    placeholder="Enter your email address"
                    className="bg-background-light dark:bg-black border-border-light dark:border-[#1A1D24] text-text-primary-light dark:text-[#02DFC2] h-11 text-[12px] placeholder-text-muted-light dark:placeholder:text-slate-800 focus:border-[#02DFC2]"
                    leftIcon={<Mail className="h-4 w-4 text-text-muted-light dark:text-slate-600" />}
                    error={error}
                    autoComplete="email"
                    disabled={isLoading}
                />

                <Button
                    type="submit"
                    className="w-full bg-[#02DFC2] text-black font-black tracking-[0.2em] h-11 shadow-[0_0_15px_rgba(2,223,194,0.2)] hover:bg-[#00c9ae]"
                    size="lg"
                    loading={isLoading}
                >
                    SEND VERIFICATION CODE
                </Button>
            </form>

            {/* Footer */}
            <div className="text-center pt-2">
                <Link
                    to="/auth/login"
                    className="inline-flex items-center space-x-2 text-[10px] text-slate-500 hover:text-[#02DFC2] transition-colors duration-200 uppercase tracking-widest"
                >
                    <ArrowLeft className="w-3 h-3" />
                    <span>Back to login</span>
                </Link>
            </div>
        </div>
    );
};

export default ForgotPassword;